import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { io } from "socket.io-client";
import apiClient from "../utils/apiClient";
import styled from "@emotion/styled";
import { client, urlFor } from "../sanity/sanityClient";
import { host } from "../utils/APIRoutes";
import { useStateContext } from "../context/StateContext";
import { toggleFollow, checkIsFollowing } from "../services/feed";
import { Room, RoomEvent, Track } from "livekit-client";
import Cart from "../components/Cart";
import {
  FiMenu,
  FiShoppingBag,
  FiGrid,
  FiRadio,
  FiUser,
  FiSend,
} from "react-icons/fi";
import { AiOutlineEye, AiOutlineHeart, AiFillHeart, AiOutlineGift } from "react-icons/ai";
import { MdDragIndicator } from "react-icons/md";
import useOnlineStatus from "../useOnlineStatus";

/**
 * Bridgen Live — social commerce live shopping.
 *
 * Covers the four phases in Bridgen_Live_User_Journey.docx:
 *  Phase 1 (Discovery): handled upstream by the push notification +
 *    the PiP/LIVE badge entry points elsewhere in the app (see the
 *    UploadForm.js top-bar link this component is reached from).
 *  Phase 2 (Engagement): real-time chat, welcome message, quick
 *    replies, heart reactions, surprise-unboxing countdown — all here.
 *  Phase 3 (Purchase): featured product card, color selection, Buy
 *    Now wired into the same cart context used by Categories.js.
 *  Phase 4 (Fulfillment): out of scope for this component — that's
 *    the existing order/escrow/delivery flow (OrdersList, Shop, etc).
 */

const QUICK_REPLIES_FALLBACK = ["Loved it! ❤️", "Need this! 🔥", "How much? 🤔"];

const formatCountdown = (ms) => {
  if (ms <= 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

// LiveKit requires an identity string for every participant, including
// anonymous viewers who aren't logged in. Persist a stable per-browser
// guest id in localStorage so the same anonymous viewer reconnecting
// (e.g. after a network blip) is recognized as the same participant
// rather than spawning a new one on every reconnect.
const getOrCreateGuestId = () => {
  const key = "bridgen_live_guest_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = `guest-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem(key, id);
  }
  return id;
};

export default function Live() {
  const navigate = useNavigate();
  const { sessionId } = useParams(); // /live/:sessionId — no more "guess the most recent"
  const socket = useRef();
  const chatEndRef = useRef(null);
  const isOnline = useOnlineStatus(() => {
    // On reconnect, re-fetch the session (in case the host pinned a
    // new product or the stream ended while we were offline) and
    // rejoin the room so viewer count / chat resume.
    fetchSession();
    if (session?._id) {
      socket.current?.emit("join-live-room", {
        sessionId: session._id,
        userId: currentUser?._id,
        username: currentUser?.username || "Guest",
      });
    }
  });

  const { totalQuantities, onAdd, showCart, setShowCart } = useStateContext();

  const [currentUser, setCurrentUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [heartCount, setHeartCount] = useState(0);
  const [floatingHearts, setFloatingHearts] = useState([]);

  const [timeLeft, setTimeLeft] = useState(null);
  const [reveal, setReveal] = useState(false);

  const [selectedColorIndex, setSelectedColorIndex] = useState(0);

  // ---- Featured product card: draggable, constrained to the top
  // half of the screen (its bottom edge may never cross the 50%
  // vertical mark, keeping it clear of the chat/input/nav stack). ----
  const productCardRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [cardPosition, setCardPosition] = useState(null); // null = default CSS position (bottom-left) until first dragged
  const [isDraggingCard, setIsDraggingCard] = useState(false);

  const [isFollowingHost, setIsFollowingHost] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  // ---- LiveKit (live camera viewing) — a single Room connection to
  // whichever host is broadcasting this session. Nothing here is ever
  // written to disk/IndexedDB/a database — it's live media only, for
  // as long as the connection is open. ----
  const remoteVideoRef = useRef(null);
  const roomRef = useRef(null);
  const [webrtcConnected, setWebrtcConnected] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  // ---------------------------------------------------------------
  // Load current user (same pattern as Chat.js)
  // ---------------------------------------------------------------
  useEffect(() => {
    const userData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);
    if (userData) setCurrentUser(JSON.parse(userData));
  }, []);

  // ---------------------------------------------------------------
  // Fetch the SPECIFIC session named in the route. Previously this
  // fetched "whichever session started most recently" — broken the
  // moment two merchants are live at once, since a viewer following
  // a link (or picked from LiveDiscovery) to Merchant A's stream
  // would silently land on Merchant B's if B went live more recently.
  // No sessionId in the route (e.g. an old bookmark) sends the viewer
  // to the discovery screen instead of guessing.
  // ---------------------------------------------------------------
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      navigate("/live-discovery", { replace: true });
      return;
    }

    setLoadingSession(true);
    try {
      const doc = await client.fetch(
        `*[_type == "liveSession" && _id == $id][0]{
          ...,
          "recordingAssetUrl": recordingAsset.asset->url
        }`,
        { id: sessionId }
      );

      if (!doc) {
        setNotFound(true);
        setSession(null);
        return;
      }

      let featuredProduct = null;
      if (doc.featuredProductId) {
        featuredProduct = await client.fetch(
          `*[_id == $id][0]{ _id, name, price, currency, image, colors, slug, status }`,
          { id: doc.featuredProductId }
        );
      }

      setSession({ ...doc, featuredProduct });
      setViewerCount(doc.seedViewerCount || 0);
      setNotFound(false);
    } catch (err) {
      console.error("Failed to load live session:", err);
    } finally {
      setLoadingSession(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // ---------------------------------------------------------------
  // Follow status for this session's host
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!currentUser?._id || !session?.hostUserId) return;
    if (currentUser._id === session.hostUserId) return; // can't follow yourself

    let cancelled = false;
    checkIsFollowing(currentUser._id, session.hostUserId)
      .then((result) => {
        if (!cancelled) setIsFollowingHost(result);
      })
      .catch((err) => console.error("Follow status check failed:", err));
    return () => {
      cancelled = true;
    };
  }, [currentUser?._id, session?.hostUserId]);

  const handleFollowToggle = async () => {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    if (!session?.hostUserId || followBusy) return;

    setFollowBusy(true);
    const optimistic = !isFollowingHost;
    setIsFollowingHost(optimistic);

    try {
      const result = await toggleFollow(currentUser._id, session.hostUserId);
      setIsFollowingHost(result.following);
    } catch (err) {
      console.error("Follow toggle failed:", err);
      setIsFollowingHost(!optimistic);
    } finally {
      setFollowBusy(false);
    }
  };

  // ---------------------------------------------------------------
  // Socket connection + live room join, mirroring Chat.js's existing
  // io(host) pattern for consistency with the rest of the app.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!session?._id) return;

    socket.current = io(host);

    socket.current.emit("join-live-room", {
      sessionId: session._id,
      userId: currentUser?._id,
      username: currentUser?.username || "Guest",
    });

    socket.current.on("live-chat-message", (msg) => {
      setMessages((prev) => [...prev, msg]);
    });

    socket.current.on("live-viewer-count", ({ count }) => {
      setViewerCount(count);
    });

    socket.current.on("live-heart-update", ({ count }) => {
      setHeartCount(count);
    });

    socket.current.on("live-product-pinned", async ({ productId }) => {
      try {
        const featuredProduct = await client.fetch(
          `*[_id == $id][0]{ _id, name, price, currency, image, colors, slug, status }`,
          { id: productId }
        );
        setSession((prev) => (prev ? { ...prev, featuredProduct } : prev));
        setSelectedColorIndex(0);
      } catch (err) {
        console.error("Failed to load newly pinned product:", err);
      }
    });

    socket.current.on("live-ended", () => {
      setSession((prev) => (prev ? { ...prev, isLive: false } : prev));
    });

    // Host can set/update the surprise-unboxing time mid-stream (see
    // the GoLiveControlPanel) — without this listener, only viewers
    // who joined AFTER that update (and therefore fetched it fresh
    // from Sanity) would ever see the countdown.
    socket.current.on("live-surprise-updated", ({ surpriseUnboxingAt }) => {
      setSession((prev) => (prev ? { ...prev, surpriseUnboxingAt } : prev));
      setReveal(false);
    });

    return () => {
      socket.current?.emit("leave-live-room", { sessionId: session._id });
      socket.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?._id]);

  // ---------------------------------------------------------------
  // LiveKit — the host's camera/mic, when streamMode is 'webrtc'.
  // Deliberately a SEPARATE effect from the chat socket above: video
  // connectivity now goes through LiveKit's own server entirely, our
  // Socket.IO server no longer carries any offer/answer/ICE traffic
  // at all — it's only responsible for chat/hearts/product-pin/
  // surprise-timer, same as when streamMode is 'image' or 'external'.
  // ---------------------------------------------------------------
  useEffect(() => {
     if (!session?._id || session.streamMode !== "webrtc" || !session.isLive) return;

  let cancelled = false;
  const room = new Room();
  roomRef.current = room;

  const videoElement = remoteVideoRef.current;

  room.on(RoomEvent.TrackSubscribed, (track) => {
    if (track.kind === Track.Kind.Video && videoElement) {
      track.attach(videoElement);

      videoElement
        .play()
        .then(() => setAutoplayBlocked(false))
        .catch(() => setAutoplayBlocked(true));

      setWebrtcConnected(true);
    }

    if (track.kind === Track.Kind.Audio && videoElement) {
      track.attach(videoElement);
    }
  });

  room.on(RoomEvent.TrackUnsubscribed, (track) => {
    track.detach();
  });

  room.on(RoomEvent.Disconnected, () => {
    if (!cancelled) setWebrtcConnected(false);
  });

    (async () => {
      try {
        const identity = currentUser?._id || getOrCreateGuestId();
        const { data } = await apiClient.post(`${host}/api/live/token`, {
          roomName: session._id,
          identity,
          name: currentUser?.username || "Guest",
          canPublish: false, // viewers only ever subscribe, never publish — enforced server-side too
        });

        if (cancelled) return;
        await room.connect(data.url, data.token);
      } catch (err) {
        console.error("Failed to connect to live stream:", err);
      }
    })();

          return () => {
    cancelled = true;
    room.disconnect();
    roomRef.current = null;
    setWebrtcConnected(false);

    if (videoElement) {
      videoElement.srcObject = null;
    }
  };
  }, [session?._id, session?.streamMode, session?.isLive, currentUser]);

  // ---------------------------------------------------------------
  // Surprise unboxing countdown
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!session?.surpriseUnboxingAt) {
      setTimeLeft(null);
      return;
    }

    const target = new Date(session.surpriseUnboxingAt).getTime();

    const tick = () => {
      const remaining = target - Date.now();
      setTimeLeft(remaining);
      if (remaining <= 0) setReveal(true);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [session?.surpriseUnboxingAt]);

  // ---------------------------------------------------------------
  // Auto-scroll chat to latest message
  // ---------------------------------------------------------------
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ---------------------------------------------------------------
  // Send a chat message (typed or via a quick-reply chip)
  // ---------------------------------------------------------------
  const sendMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed || !session?._id || !isOnline) return;

      socket.current?.emit("live-chat-message", {
        sessionId: session._id,
        userId: currentUser?._id,
        username: currentUser?.username || "Guest",
        text: trimmed,
        isHost: currentUser?._id && currentUser._id === session.hostUserId,
      });

      setMessageText("");
    },
    [session, currentUser, isOnline]
  );

  const handleSendClick = () => sendMessage(messageText);

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendMessage(messageText);
    }
  };

  // ---------------------------------------------------------------
  // Heart tap — optimistic local bump + floating animation, plus the
  // real broadcast-driven count from the server via live-heart-update.
  // ---------------------------------------------------------------
  const handleHeartTap = () => {
    if (!session?._id) return;
    socket.current?.emit("live-heart", { sessionId: session._id });

    const id = Date.now() + Math.random();
    setFloatingHearts((prev) => [...prev, { id, left: 20 + Math.random() * 40 }]);
    setTimeout(() => {
      setFloatingHearts((prev) => prev.filter((h) => h.id !== id));
    }, 1600);
  };

  // ---------------------------------------------------------------
  // Buy Now — reuses the same cart context as Categories.js, so the
  // Cart modal, checkout flow, and order creation are all identical
  // to the rest of the app; nothing live-specific to build there.
  // ---------------------------------------------------------------
  const handleBuyNow = () => {
    const product = session?.featuredProduct;
    if (!product) return;

    const productForCart = {
      ...product,
      quantity: 1,
      selectedIndex: selectedColorIndex,
    };

    const result = onAdd(productForCart, 1, selectedColorIndex);
    if (result?.success) {
      setShowCart(true);
    }
  };

  // ---------------------------------------------------------------
  // Featured product card drag — clamps so the card's bottom edge
  // can never cross the vertical midpoint of the viewport, keeping it
  // out of the chat/quick-reply/input/nav stack that lives in the
  // bottom half. Uses Pointer Events + setPointerCapture so the same
  // handlers work for mouse, touch, and pen without separate touch
  // listeners, and keep receiving move/up events even if the pointer
  // leaves the drag handle mid-drag.
  // ---------------------------------------------------------------
  const clampCardPosition = useCallback((top, left) => {
    const card = productCardRef.current;
    const cardWidth = card?.offsetWidth || 150;
    const cardHeight = card?.offsetHeight || 220;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const minTop = 92; // stay clear of TopBar/StatusRow/HostRow
    const maxTop = Math.max(viewportH * 0.5 - cardHeight, minTop); // never let the card's bottom cross the 50% line
    const minLeft = 8;
    const maxLeft = Math.max(viewportW - cardWidth - 8, minLeft);

    return {
      top: Math.min(Math.max(top, minTop), maxTop),
      left: Math.min(Math.max(left, minLeft), maxLeft),
    };
  }, []);

  const handleCardPointerDown = (e) => {
    const card = productCardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setIsDraggingCard(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleCardPointerMove = (e) => {
    if (!isDraggingCard) return;
    const rawLeft = e.clientX - dragOffsetRef.current.x;
    const rawTop = e.clientY - dragOffsetRef.current.y;
    setCardPosition(clampCardPosition(rawTop, rawLeft));
  };

  const handleCardPointerUp = (e) => {
    setIsDraggingCard(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Re-clamp on resize/orientation change so a previously-dragged
  // position doesn't end up stranded off-screen or below the 50%
  // line after the viewport dimensions change.
  useEffect(() => {
    const handleResize = () => {
      setCardPosition((prev) => (prev ? clampCardPosition(prev.top, prev.left) : prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampCardPosition]);

  const quickReplies =
    session?.quickReplies?.length ? session.quickReplies : QUICK_REPLIES_FALLBACK;

  const coverImageUrl = session?.coverImage ? urlFor(session.coverImage) : null;
  const hostAvatarUrl = session?.hostAvatarUrl || null;

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  if (loadingSession && !session) {
    return (
      <Container>
        <CenterMessage>Loading live session…</CenterMessage>
      </Container>
    );
  }

  if (notFound || !session) {
    return (
      <Container>
        <TopBar>
          <FiMenu size={22} color="#fff" onClick={() => navigate(-1)} />
          <Logo>
            Bridgen <span>Live</span>
          </Logo>
          <div style={{ width: 22 }} />
        </TopBar>
        <CenterMessage>
          No live session right now — check back soon!
          <br />
          <Link to="/" style={{ color: "#f97316", fontWeight: 600 }}>
            Browse products instead
          </Link>
        </CenterMessage>
        {/* No active session here, so Shop has no host to point at —
            BottomNav falls back to "/" when hostUserId is absent. */}
        <BottomNav currentUser={currentUser} />
      </Container>
    );
  }

  // ---------------------------------------------------------------
  // Stream has ended. Two sub-cases:
  //  - A replay exists and is still within its 24-hour window ->
  //    show it, same idea as tapping into an expired-but-recent
  //    WhatsApp Status.
  //  - No replay (host picked cover image/external, recording never
  //    completed, or the 24-hour cleanup job already deleted it) ->
  //    a plain "ended" message, same visual language as the
  //    no-session state above.
  // ---------------------------------------------------------------
  if (!session.isLive) {
    const hasValidReplay =
      session.streamMode === "webrtc" &&
      session.recordingAssetUrl &&
      session.recordingExpiresAt &&
      new Date(session.recordingExpiresAt).getTime() > Date.now();

    if (hasValidReplay) {
      return (
        <Container>
          <TopBar>
            <FiMenu size={22} color="#fff" onClick={() => navigate(-1)} />
            <Logo>
              Bridgen <span>Live</span>
            </Logo>
            <div style={{ width: 22 }} />
          </TopBar>

          <ReplayBadge>REPLAY</ReplayBadge>

          <ReplayVideo src={session.recordingAssetUrl} controls playsInline />

          <HostRow>
            {session.hostAvatarUrl ? (
              <HostAvatarImg
                src={
                  session.hostAvatarUrl.startsWith("http")
                    ? session.hostAvatarUrl
                    : `data:image/svg+xml;base64,${session.hostAvatarUrl}`
                }
                alt={session.hostName}
              />
            ) : (
              <HostAvatarPlaceholder>{session.hostName?.charAt(0)?.toUpperCase()}</HostAvatarPlaceholder>
            )}
            <HostNameText style={{ color: "#111" }}>{session.hostName}</HostNameText>
          </HostRow>

          <ReplayExpiryNote>
            This replay disappears{" "}
            {new Date(session.recordingExpiresAt).toLocaleString([], {
              hour: "2-digit",
              minute: "2-digit",
              month: "short",
              day: "numeric",
            })}
          </ReplayExpiryNote>

          {session.featuredProduct && (
            <ReplayProductCard>
              {session.featuredProduct.image?.[0] && (
                <Link to={`/category/${session.featuredProduct.slug?.current}`}>
                  <ProductImage
                    src={urlFor(session.featuredProduct.image[0])}
                    alt={session.featuredProduct.name}
                  />
                </Link>
              )}
              <ProductName>{session.featuredProduct.name}</ProductName>
              <ProductPrice>
                {session.featuredProduct.currency || session.currency || "TZS"}{" "}
                {session.featuredProduct.price?.toLocaleString()}
              </ProductPrice>
              <BuyButton onClick={handleBuyNow} disabled={!isOnline}>
                {isOnline ? "BUY NOW" : "OFFLINE"}
              </BuyButton>
            </ReplayProductCard>
          )}

          <div className={`modal ${showCart ? "active" : ""}`}>
            <Cart />
          </div>

          <BottomNav currentUser={currentUser} hostUserId={session.hostUserId} />
        </Container>
      );
    }

    return (
      <Container>
        <TopBar>
          <FiMenu size={22} color="#fff" onClick={() => navigate(-1)} />
          <Logo>
            Bridgen <span>Live</span>
          </Logo>
          <div style={{ width: 22 }} />
        </TopBar>
        <CenterMessage>
          This live stream has ended.
          <br />
          <Link to="/live-discovery" style={{ color: "#f97316", fontWeight: 600 }}>
            See who's live now
          </Link>
        </CenterMessage>
        <BottomNav currentUser={currentUser} hostUserId={session.hostUserId} />
      </Container>
    );
  }

  return (
    <Container>
      {/* ---------- BACKGROUND: video if available, else cover image ---------- */}
      {/* ---------- BACKGROUND: live camera (WebRTC), external URL, or cover image ---------- */}
      {session.streamMode === "webrtc" ? (
        <>
          <BackgroundVideo ref={remoteVideoRef} autoPlay playsInline />
          {!webrtcConnected && (
            coverImageUrl ? (
              <BackgroundImage src={coverImageUrl} alt={session.title} />
            ) : (
              <BackgroundFallback />
            )
          )}
        </>
      ) : session.streamMode === "external" && session.videoUrl ? (
        <BackgroundVideo src={session.videoUrl} autoPlay muted loop playsInline />
      ) : coverImageUrl ? (
        <BackgroundImage src={coverImageUrl} alt={session.title} />
      ) : (
        <BackgroundFallback />
      )}

      {session.streamMode === "webrtc" && webrtcConnected && autoplayBlocked && (
        <TapToPlayOverlay
          onClick={() => {
            remoteVideoRef.current
              ?.play()
              .then(() => setAutoplayBlocked(false))
              .catch((err) => console.error("Manual play failed:", err));
          }}
        >
          ▶ Tap to play
        </TapToPlayOverlay>
      )}
      <Scrim />

      {!isOnline && (
        <OfflineBanner>
          You're offline — live chat is paused. Reconnecting…
        </OfflineBanner>
      )}

      {/* ---------- TOP BAR ---------- */}
      <TopBar>
        <FiMenu size={22} color="#fff" onClick={() => navigate(-1)} />
        <Logo>
          Bridgen <span>Live</span>
        </Logo>
        <CartIcon onClick={() => setShowCart(true)}>
          <FiShoppingBag size={22} color="#fff" />
          {totalQuantities > 0 && <CartBadge>{totalQuantities}</CartBadge>}
        </CartIcon>
      </TopBar>

      {/* ---------- LIVE + VIEWERS ---------- */}
      <StatusRow>
        <LiveBadge>LIVE</LiveBadge>
        <ViewerPill>
          <AiOutlineEye size={16} />
          {viewerCount >= 1000 ? `${(viewerCount / 1000).toFixed(1)}K` : viewerCount}
        </ViewerPill>
      </StatusRow>

      {/* ---------- HOST ROW ---------- */}
      <HostRow>
        {hostAvatarUrl ? (
          <HostAvatarImg
            src={hostAvatarUrl.startsWith("http") ? hostAvatarUrl : `data:image/svg+xml;base64,${hostAvatarUrl}`}
            alt={session.hostName}
          />
        ) : (
          <HostAvatarPlaceholder>{session.hostName?.charAt(0)?.toUpperCase()}</HostAvatarPlaceholder>
        )}
        <HostNameText>{session.hostName}</HostNameText>
        {currentUser?._id !== session.hostUserId && (
          <FollowHostButton followed={isFollowingHost} disabled={followBusy} onClick={handleFollowToggle}>
            {isFollowingHost ? "Following" : "Follow"}
          </FollowHostButton>
        )}
      </HostRow>

      {/* ---------- SURPRISE UNBOXING COUNTDOWN ---------- */}
      {session.surpriseUnboxingAt && !reveal && timeLeft !== null && (
        <SurpriseCard>
          <AiOutlineGift size={26} color="#f97316" />
          <SurpriseLabel>SURPRISE UNBOXING</SurpriseLabel>
          <SurpriseSub>Starts in</SurpriseSub>
          <SurpriseTimer>{formatCountdown(timeLeft)}</SurpriseTimer>
        </SurpriseCard>
      )}
      {reveal && (
        <SurpriseCard>
          <AiOutlineGift size={26} color="#f97316" />
          <SurpriseLabel>REVEALING NOW! 🎉</SurpriseLabel>
        </SurpriseCard>
      )}

      {/* ---------- FEATURED PRODUCT CARD (draggable, top-half only) ---------- */}
      {isDraggingCard && <DragBoundaryLine />}
      {session.featuredProduct && (
        <ProductCard
          ref={productCardRef}
          style={
            cardPosition
              ? { top: `${cardPosition.top}px`, left: `${cardPosition.left}px`, bottom: "auto" }
              : undefined
          }
        >
          <DragHandle
            onPointerDown={handleCardPointerDown}
            onPointerMove={handleCardPointerMove}
            onPointerUp={handleCardPointerUp}
            dragging={isDraggingCard}
          >
            <MdDragIndicator size={16} />
          </DragHandle>

          <FeaturedTag>FEATURED</FeaturedTag>
          {session.featuredProduct.image?.[0] && (
            <Link to={`/category/${session.featuredProduct.slug?.current}`}>
              <ProductImage
                src={urlFor(session.featuredProduct.image[0])}
                alt={session.featuredProduct.name}
              />
            </Link>
          )}
          <ProductName>{session.featuredProduct.name}</ProductName>
          <ProductPrice>
            {session.featuredProduct.currency || session.currency || "TZS"}{" "}
            {session.featuredProduct.price?.toLocaleString()}
          </ProductPrice>

          {Array.isArray(session.featuredProduct.colors) &&
            session.featuredProduct.colors.length > 0 && (
              <ColorRow>
                {session.featuredProduct.colors.map((c, idx) => (
                  <ColorSwatch
                    key={idx}
                    style={{ backgroundColor: c }}
                    selected={idx === selectedColorIndex}
                    onClick={() => setSelectedColorIndex(idx)}
                  />
                ))}
              </ColorRow>
            )}

          <BuyButton onClick={handleBuyNow} disabled={!isOnline}>
            {isOnline ? "BUY NOW" : "OFFLINE"}
          </BuyButton>
        </ProductCard>
      )}

      {/* ---------- CHAT OVERLAY ---------- */}
      <ChatOverlay>
        {messages.slice(-30).map((msg) => (
          <ChatBubble key={msg._id} isHost={msg.isHost} isSystem={msg.fromSystem}>
            <ChatRow>
              <ChatName isHost={msg.isHost}>
                {msg.username}
                {msg.isHost && <HostTag>HOST</HostTag>}
              </ChatName>
              <ChatTime>
                {new Date(msg.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </ChatTime>
            </ChatRow>
            <ChatText>{msg.text}</ChatText>
          </ChatBubble>
        ))}
        <div ref={chatEndRef} />
      </ChatOverlay>

      {/* ---------- FLOATING HEARTS ---------- */}
      {floatingHearts.map((h) => (
        <FloatingHeart key={h.id} style={{ left: `${h.left}%` }}>
          <AiFillHeart size={22} color="#f97316" />
        </FloatingHeart>
      ))}

      {/* ---------- QUICK REPLIES ---------- */}
      <QuickReplyRow>
        {quickReplies.map((reply, idx) => (
          <QuickReplyChip key={idx} onClick={() => sendMessage(reply)} disabled={!isOnline}>
            {reply}
          </QuickReplyChip>
        ))}
      </QuickReplyRow>

      {/* ---------- MESSAGE INPUT + HEART ---------- */}
      <InputRow>
        <MessageInputWrapper>
          <MessageInput
            placeholder="Type a message..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleInputKeyDown}
            disabled={!isOnline}
          />
        </MessageInputWrapper>
        <SendButton onClick={handleSendClick} disabled={!isOnline || !messageText.trim()}>
          <FiSend size={18} color="#fff" />
        </SendButton>
        <HeartButton onClick={handleHeartTap} disabled={!isOnline}>
          <AiOutlineHeart size={22} color="#fff" />
          <HeartCount>
            {heartCount >= 1000 ? `${(heartCount / 1000).toFixed(1)}K` : heartCount}
          </HeartCount>
        </HeartButton>
      </InputRow>

      <div className={`modal ${showCart ? "active" : ""}`}>
        <Cart />
      </div>

      <BottomNav currentUser={currentUser} hostUserId={session.hostUserId} />
    </Container>
  );
}

// A tiny presentational component kept in this file since it's
// specific to the Live layout (highlights the "Live" tab as active).
function BottomNav({ currentUser, hostUserId }) {
  // While watching a live session, "Shop" should take the viewer
  // straight to that host's storefront (/shop/:hostUserId) rather
  // than the generic home feed — that's the whole point of tapping
  // Shop mid-stream: buying more from the person you're currently
  // watching. Falls back to "/" when there's no active session to
  // anchor it to (e.g. the "no live session right now" screen).
  const shopLink = hostUserId ? `/shop/${hostUserId}` : "/";

  return (
    <NavBar>
      <NavItem to={shopLink}>
        <FiShoppingBag size={20} />
        <span>Shop</span>
      </NavItem>
      <NavItem to="/">
        <FiGrid size={20} />
        <span>Categories</span>
      </NavItem>
      <NavItem to="/live-discovery" active>
        <FiRadio size={20} />
        <span>Live</span>
      </NavItem>
      <NavItem to={currentUser ? `/shop/${currentUser._id}` : "/login"}>
        <FiUser size={20} />
        <span>Profile</span>
      </NavItem>
    </NavBar>
  );
}

/* ================= STYLES ================= */

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #000;
  display: flex;
  flex-direction: column;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BackgroundFallback = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, #d9a679, #e8d8d2);
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 40%, rgba(0, 0, 0, 0.55) 100%);
`;

const TapToPlayOverlay = styled.button`
  position: absolute;
  inset: 0;
  z-index: 4;
  width: 100%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
`;

const OfflineBanner = styled.div`
  position: relative;
  z-index: 5;
  background: #444;
  color: white;
  text-align: center;
  font-size: 12px;
  padding: 4px;
`;

const TopBar = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  span {
    color: #f97316;
  }
`;

const CartIcon = styled.div`
  position: relative;
  cursor: pointer;
`;

const CartBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  background: #f97316;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusRow = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
`;

const LiveBadge = styled.div`
  background: #f97316;
  color: white;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 20px;
`;

const ViewerPill = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
`;

const HostRow = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px 0;
`;

const HostAvatarImg = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.6);
`;

const HostAvatarPlaceholder = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f97316;
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.6);
`;

const HostNameText = styled.span`
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const FollowHostButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${(props) => (props.followed ? "rgba(255,255,255,0.2)" : "#f97316")};
  color: white;
  border: 1px solid ${(props) => (props.followed ? "rgba(255,255,255,0.5)" : "#f97316")};
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  margin-left: 4px;
`;

const SurpriseCard = styled.div`
  position: absolute;
  top: 60px;
  right: 16px;
  z-index: 5;
  background: rgba(20, 20, 20, 0.85);
  border: 1px solid #f97316;
  border-radius: 12px;
  padding: 12px 16px;
  text-align: center;
  color: white;
  width: 150px;
`;

const SurpriseLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

const SurpriseSub = styled.div`
  font-size: 10px;
  color: #ccc;
  margin-top: 4px;
`;

const SurpriseTimer = styled.div`
  background: #f97316;
  border-radius: 8px;
  padding: 4px;
  font-size: 20px;
  font-weight: 800;
  margin-top: 4px;
`;

const DragBoundaryLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  z-index: 4;
  border-top: 1px dashed rgba(255, 255, 255, 0.5);
  pointer-events: none;
`;

const DragHandle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  margin: -4px -4px 4px;
  border-radius: 6px 6px 0 0;
  color: #bbb;
  cursor: ${(props) => (props.dragging ? "grabbing" : "grab")};
  touch-action: none; /* prevent the browser's own touch-scroll from fighting the drag */
  background: ${(props) => (props.dragging ? "#f3f4f6" : "transparent")};
`;

const ProductCard = styled.div`
  position: absolute;
  right: 16px;
  bottom: 48%;
  z-index: 5;
  width: 150px;
  background: white;
  border-radius: 14px;
  padding: 10px;
  text-align: left;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
`;

const FeaturedTag = styled.div`
  display: inline-block;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  margin-bottom: 6px;
`;

const ProductImage = styled.img`
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
`;

const ProductName = styled.p`
  font-size: 12px;
  font-weight: 600;
  margin: 6px 0 2px;
  color: #111;
`;

const ProductPrice = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: #f97316;
  margin: 0 0 6px;
`;

const ColorRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
`;

const ColorSwatch = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid ${(props) => (props.selected ? "#f97316" : "#ddd")};
`;

const BuyButton = styled.button`
  width: 100%;
  background: ${(props) => (props.disabled ? "#ccc" : "#f97316")};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 0;
  font-size: 12px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const ChatOverlay = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 168px; /* clears QuickReplyRow (116px) + its own height */
  z-index: 4;
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChatBubble = styled.div`
  background: ${(props) =>
    props.isHost ? "rgba(37, 99, 235, 0.85)" : "rgba(0, 0, 0, 0.5)"};
  color: white;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  max-width: 90%;
  ${(props) => props.isSystem && "font-style: italic; opacity: 0.8;"}
`;

const ChatRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const ChatName = styled.span`
  font-weight: 700;
  font-size: 11px;
  color: ${(props) => (props.isHost ? "#fef3c7" : "#f5f5f5")};
`;

const HostTag = styled.span`
  background: #f97316;
  font-size: 9px;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 6px;
`;

const ChatTime = styled.span`
  font-size: 10px;
  color: #ccc;
`;

const ChatText = styled.div`
  margin-top: 2px;
`;

const FloatingHeart = styled.div`
  position: absolute;
  bottom: 110px; /* aligns with HeartButton in the repositioned InputRow */
  z-index: 6;
  animation: floatUp 1.6s ease-out forwards;

  @keyframes floatUp {
    0% {
      opacity: 1;
      transform: translateY(0) scale(0.8);
    }
    100% {
      opacity: 0;
      transform: translateY(-160px) scale(1.3);
    }
  }
`;

const QuickReplyRow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 116px; /* directly above InputRow (64px nav + ~52px input row) */
  z-index: 5;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 14px 8px;
  white-space: nowrap;
`;

const QuickReplyChip = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 12px;
  flex-shrink: 0;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const InputRow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 64px; /* directly above the fixed BottomNav */
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
`;

const MessageInputWrapper = styled.div`
  flex: 1;
`;

const MessageInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 10px 16px;
  color: white;
  font-size: 13px;
  outline: none;

  &::placeholder {
    color: #ddd;
  }
`;

const SendButton = styled.button`
  background: #2563eb;
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  flex-shrink: 0;
`;

const HeartButton = styled.button`
  background: #f97316;
  border: none;
  border-radius: 50%;
  width: 46px;
  height: 46px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  flex-shrink: 0;
`;

const HeartCount = styled.span`
  color: white;
  font-size: 9px;
  font-weight: 700;
  margin-top: 1px;
`;

const NavBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  height: 64px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  background: #0f1f3d;
`;

const NavItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  color: ${(props) => (props.active ? "#f97316" : "#cbd5e1")};
  text-decoration: none;
  font-size: 10px;
`;

const CenterMessage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  padding: 20px;
  font-size: 14px;
`;

const ReplayBadge = styled.div`
  align-self: center;
  margin-top: 12px;
  background: #2563eb;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 20px;
`;

const ReplayVideo = styled.video`
  width: 100%;
  max-height: 60vh;
  background: #000;
  margin-top: 12px;
`;

const ReplayExpiryNote = styled.p`
  text-align: center;
  color: #999;
  font-size: 11px;
  margin: 8px 0 0;
`;

const ReplayProductCard = styled.div`
  margin: 16px auto;
  width: 220px;
  background: white;
  border: 1px solid #eee;
  border-radius: 14px;
  padding: 14px;
  text-align: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
`;
